import { useEffect } from "react";

// How long a person spends on the inbox (design/plan.md §6, attention; plan
// M5 step 5e). While a review is open, a second counts only when the tab is
// visible and focused and there was input in the last minute. Seconds go to
// the server in small batches, and when the review closes or the tab hides.

const IDLE_MS = 60_000;
const BATCH = 30;
const INPUTS = ["pointerdown", "pointermove", "keydown", "wheel", "scroll"] as const;

/** Counts attention on one product's review; nothing is counted without one. */
export function useAttention(productId: string | null) {
  useEffect(() => {
    if (!productId) return;
    let seconds = 0;
    let lastInput = Date.now();
    const onInput = () => { lastInput = Date.now(); };
    const send = (keepalive: boolean) => {
      if (seconds < 1) return;
      const body = JSON.stringify({ product_id: productId, seconds: Math.min(seconds, 900) });
      seconds = 0;
      // A lost batch only undercounts; it never blocks the review.
      void fetch("/api/attention", { method: "POST", headers: { "content-type": "application/json" }, body, credentials: "same-origin", keepalive }).catch(() => {});
    };
    const tick = window.setInterval(() => {
      if (document.visibilityState === "visible" && document.hasFocus() && Date.now() - lastInput < IDLE_MS) seconds += 1;
      if (seconds >= BATCH) send(false);
    }, 1000);
    const onHide = () => { if (document.visibilityState === "hidden") send(true); };
    const onPageHide = () => send(true);
    for (const name of INPUTS) window.addEventListener(name, onInput, { passive: true, capture: true });
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.clearInterval(tick);
      for (const name of INPUTS) window.removeEventListener(name, onInput, { capture: true });
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      send(true);
    };
  }, [productId]);
}
